<template>
  <div class="miss-print">
    <Modal
      class="miss-print__modal"
      @close="closeModal"
    >
      <div class="miss-print__content">
        <div
          v-if="status === 'success'"
          class="miss-print__success"
        >
          <div class="miss-print__success-image" />
          <div class="miss-print__success-text">
            <p>Вы сообщили об ошибке в тексте.</p>
            <p>Спасибо за помощь!</p>
          </div>
        </div>
        <div
          v-else-if="status === 'error'"
          class="miss-print__error"
        >
          <div class="miss-print__error-text">
            <p>Ошибка: {{ errorMessage }}</p>
            <p>Попоробуйте позже</p>
          </div>
        </div>
        <div
          v-else
          class="miss-print__form-content"
        >
          <form
            class="miss-print__form"
            @submit.prevent="onSubmit"
          >
            <p class="miss-print__selection">«{{ computedSelection }}»</p>
            <input
              v-model="comment"
              :maxlength="commentMaxLength"
              autofocus
              class="miss-print__comment"
              placeholder="Комментарий (необязательно)"
              type="text"
            />
            <div class="miss-print__buttons">
              <BaseButton
                tag="button"
                type="submit"
              >
                Отправить
              </BaseButton>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import Modal from '~/components/Modal.vue';
  import type { IMissPrintResponseError, IMissPrintResponseSuccess } from '~/types/api/missPrint';
  import { MISSPRINT_SELECTION_MAX_LENGTH } from '~/constants';
  import BaseButton from '@devhacker/shared/components/base/BaseButton.vue';

  export default defineNuxtComponent({
    name: 'MissPrint',

    components: {
      Modal,
      BaseButton,
    },

    props: {
      selection: {
        type: String as PropType<string>,
        default: '',
      },

      missprint: {
        type: String as PropType<string>,
        default: '',
      },

      link: {
        type: String as PropType<string>,
        required: true,
      },
    },

    data: () => ({
      commentMaxLength: 255,
      comment: '',
      status: null as null | 'success' | 'error' | 'pending',
      errorMessage: '',
      timeToCloseModal: 2000, // ms
    }),

    computed: {
      computedSelection(): string {
        const selection = this.selection.trim();
        return selection.length > MISSPRINT_SELECTION_MAX_LENGTH
          ? `${selection.substring(0, MISSPRINT_SELECTION_MAX_LENGTH)}...`
          : selection;
      },
    },

    methods: {
      closeModal() {
        this.$emit('close');
      },

      onSubmit() {
        if (this.status === 'pending') return;

        this.status = 'pending';
        const payload = {
          missprint: this.missprint,
          link: this.link,
          ...(this.comment
            ? {
                comment: this.comment,
              }
            : {}),
        };

        this.$api.missPrint
          .sendAlert(payload)
          .then((_response: IMissPrintResponseSuccess | IMissPrintResponseError): any => {
            this.status = 'success';
            setTimeout(() => {
              this.closeModal();
            }, this.timeToCloseModal);
          })
          .catch((error: Error) => {
            this.status = 'error';
            const errorMessage = (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message;
            this.errorMessage = errorMessage;
          });
      },
    },
  });
</script>

<style lang="scss" scoped>
  .miss-print {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: auto;

    &__modal {
      height: 320px;
      max-width: 640px;
      width: 640px;
      padding: 78px 24px;
      font-family: $text-font--1;
    }

    &__content {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 640px;
    }

    &__selection {
      @include bigText;
      font-weight: 500;
      margin-bottom: 16px;
    }

    &__comment {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid $border-color--2;
      border-radius: 8px;
      margin-bottom: $spacing-6;
      @include normalText;

      @include hover {
        border-color: $hover-color--1;
      }
    }

    &__buttons {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__success {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      &-image {
        width: 160px;
        height: 160px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160' fill='none'%3E%3Crect width='160' height='160' fill='white'/%3E%3Cg clip-path='url(%23clip0_5359_874)'%3E%3Cpath d='M103.707 22.6228H21.6213V129.115H103.707V22.6228Z' fill='%23C722CA'/%3E%3Cpath d='M123.274 30.8848H40.3596V137.377H123.274V30.8848Z' fill='white'/%3E%3Cpath d='M123.903 138H39.7383V30.262H123.895V138H123.903ZM40.9809 136.754H122.652V31.5075H40.9809V136.754Z' fill='%233C3C3C'/%3E%3Cpath d='M105.919 44.9592H51.9819V46.2048H105.919V44.9592Z' fill='%233C3C3C'/%3E%3Cpath d='M105.919 51.6104H51.9819V52.8559H105.919V51.6104Z' fill='%233C3C3C'/%3E%3Cpath d='M87.5781 58.2532H51.9819V59.4987H87.5781V58.2532Z' fill='%233C3C3C'/%3E%3Cpath d='M78.474 71.5471H51.9819V72.7926H78.474V71.5471Z' fill='%233C3C3C'/%3E%3Cpath d='M78.474 78.1899H51.9819V79.4355H78.474V78.1899Z' fill='%233C3C3C'/%3E%3Cpath d='M80.4953 84.8408H51.9819V86.0863H80.4953V84.8408Z' fill='%233C3C3C'/%3E%3Cpath d='M87.5781 91.4839H51.9819V92.7294H87.5781V91.4839Z' fill='%233C3C3C'/%3E%3Cpath d='M88.0171 98.1348H51.9819V99.3803H88.0171V98.1348Z' fill='%233C3C3C'/%3E%3Cpath d='M104.958 107.667H51.9819V108.913H104.958V107.667Z' fill='%233C3C3C'/%3E%3Cpath d='M110.856 114.31H51.9819V115.556H110.856V114.31Z' fill='%233C3C3C'/%3E%3Cpath d='M105.919 120.961H51.9819V122.207H105.919V120.961Z' fill='%233C3C3C'/%3E%3Cpath d='M119.911 89.6284L114.914 94.6367L140.395 120.177L145.391 115.169L119.911 89.6284Z' fill='url(%23paint0_diamond_5359_874)'/%3E%3Cpath d='M140.397 121.061L114.037 94.6391L119.919 88.7437L146.278 115.165L140.397 121.061ZM115.793 94.6391L140.397 119.301L144.514 115.174L119.91 90.5123L115.793 94.6391Z' fill='%233C3C3C'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M83.2621 57.9044C72.9072 68.2837 72.9072 85.1232 83.2621 95.5026C93.6171 105.882 110.417 105.882 120.772 95.5026C131.127 85.1232 131.127 68.2837 120.772 57.9044C110.417 47.525 93.6253 47.5167 83.2621 57.9044ZM116.464 91.1765C108.487 99.1728 95.5555 99.1728 87.5781 91.1765C79.6006 83.1802 79.6006 70.2185 87.5781 62.2222C95.5555 54.2259 108.487 54.2259 116.464 62.2222C124.442 70.2185 124.442 83.1802 116.464 91.1765Z' fill='url(%23paint1_diamond_5359_874)'/%3E%3Cpath d='M102.025 103.914C94.7768 103.914 87.9591 101.083 82.8313 95.9427C72.2445 85.3309 72.2445 68.0679 82.8313 57.4643C87.9591 52.3244 94.7768 49.4929 102.025 49.4929C109.274 49.4929 116.091 52.3244 121.219 57.4643C131.806 68.0762 131.806 85.3392 121.219 95.9427C116.091 101.083 109.274 103.914 102.025 103.914ZM102.025 50.7384C95.1082 50.7384 88.597 53.4371 83.7094 58.3445C73.603 68.4664 73.603 84.9406 83.7094 95.0626C88.6053 99.9699 95.1082 102.669 102.025 102.669C108.942 102.669 115.445 99.9699 120.341 95.0626C130.439 84.9406 130.439 68.4664 120.341 58.3445C115.445 53.4371 108.942 50.7384 102.025 50.7384ZM102.025 97.7861C96.6324 97.7861 91.2478 95.7268 87.1473 91.6166C78.9379 83.3961 78.9379 70.0109 87.1473 61.7821C91.1236 57.7964 96.4087 55.6043 102.025 55.6043C107.642 55.6043 112.935 57.7964 116.903 61.7821C125.113 70.0026 125.113 83.3878 116.903 91.6166C112.803 95.7268 107.41 97.7861 102.025 97.7861ZM102.025 56.8581C96.7318 56.8581 91.7614 58.9257 88.0254 62.6706C80.3047 70.4095 80.3047 83.0059 88.0254 90.7447C95.746 98.4836 108.304 98.4836 116.033 90.7447C123.754 83.0059 123.754 70.4095 116.033 62.6706C112.281 58.9174 107.31 56.8581 102.025 56.8581Z' fill='%233C3C3C'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M127.3 95.0962L148.573 116.42C150.768 118.62 150.992 121.958 149.078 123.868C147.164 125.786 143.834 125.553 141.647 123.361L120.374 102.038L127.3 95.0962Z' fill='url(%23paint2_diamond_5359_874)'/%3E%3Cpath d='M141.272 109.544L134.77 116.062L138.349 119.649L144.851 113.132L141.272 109.544Z' fill='%233C3C3C'/%3E%3Cpath d='M145.831 125.794C145.698 125.794 145.557 125.786 145.425 125.777C143.867 125.67 142.376 124.972 141.208 123.801L119.496 102.038L127.3 94.2158L149.012 115.979C150.18 117.15 150.876 118.653 150.983 120.206C151.091 121.8 150.569 123.253 149.517 124.308C148.556 125.279 147.256 125.794 145.831 125.794ZM121.252 102.038L142.086 122.921C144.033 124.872 146.974 125.105 148.639 123.428C149.434 122.63 149.832 121.518 149.741 120.289C149.658 119.035 149.086 117.814 148.125 116.859L127.291 95.9762L121.252 102.038Z' fill='%233C3C3C'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M99.5898 81.7272L93.6751 76.5292L94.3875 75.732L99.507 80.2325L111.411 67.2791L112.206 67.9932L99.5898 81.7272Z' fill='%233C3C3C'/%3E%3Cpath d='M99.6064 82.0179L93.3851 76.5459L94.3792 75.4415L99.4987 79.9503L111.403 66.9968L112.504 67.9849L99.6064 82.0179ZM93.965 76.5043L99.5732 81.4366L111.916 68.0099L111.428 67.5698L99.5235 80.5232L94.4041 76.0227L93.965 76.5043Z' fill='%233C3C3C'/%3E%3C/g%3E%3Cdefs%3E%3CradialGradient id='paint0_diamond_5359_874' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(123.791 98.5259) rotate(45.0676) scale(27.0465 5.30359)'%3E%3Cstop stop-color='%23E8AEFC'/%3E%3Cstop offset='1' stop-color='%2376DDDC'/%3E%3C/radialGradient%3E%3CradialGradient id='paint1_diamond_5359_874' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(102.017 63.4263) rotate(90) scale(39.8609 39.7647)'%3E%3Cstop stop-color='%23E8AEFC'/%3E%3Cstop offset='1' stop-color='%2376DDDC'/%3E%3C/radialGradient%3E%3CradialGradient id='paint2_diamond_5359_874' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(135.376 102.624) rotate(90) scale(22.5445 22.4935)'%3E%3Cstop stop-color='%23E8AEFC'/%3E%3Cstop offset='1' stop-color='%2376DDDC'/%3E%3C/radialGradient%3E%3CclipPath id='clip0_5359_874'%3E%3Crect width='130' height='116' fill='white' transform='translate(21 22)'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
        background-size: contain;
        background-position: center center;
        margin-bottom: 8px;
      }

      &-text {
        @include bigText;
        font-weight: 500;
        text-align: center;
      }
    }

    &__error {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      &-text {
        @include bigText;
        font-weight: 500;
        text-align: center;
      }
    }

    &__submit {
      background-color: #c722ca;
    }
  }
</style>
